/**
 * Represents the core user data returned from Firebase Authentication.
 * This class is immutable and provides controlled serialization for API usage.
 */
export interface IAuthUser {
  readonly uid: string;
  readonly email?: string | null;
  readonly displayName?: string | null;
  readonly photoURL?: string | null;
  readonly emailVerified: boolean;
  readonly isAnonymous: boolean;
}

export class AuthUser implements IAuthUser {
  readonly uid: string;
  readonly email?: string | null;
  readonly displayName?: string | null;
  readonly photoURL?: string | null;
  readonly emailVerified: boolean;
  readonly isAnonymous: boolean;

  constructor(data: IAuthUser) {
    this.uid = data.uid;
    this.email = data.email;
    this.displayName = data.displayName;
    this.photoURL = data.photoURL;
    this.emailVerified = data.emailVerified ?? false;
    this.isAnonymous = data.isAnonymous ?? false;
  }

  /**
   * Prepares the AuthUser object for JSON serialization.
   * Ensures a clean, predictable object structure for API responses.
   */
  toJSON(): IAuthUser {
    return {
      uid: this.uid,
      email: this.email,
      displayName: this.displayName,
      photoURL: this.photoURL,
      emailVerified: this.emailVerified,
      isAnonymous: this.isAnonymous,
    };
  }

  /**
   * Creates an AuthUser instance from a plain JavaScript object.
   * This is useful for hydrating objects received from an API or database.
   * @param data A plain object that conforms to the AuthUser structure.
   */
  static fromJSON(data: Record<string, unknown>): AuthUser {
    if (typeof data.uid !== 'string' || !data.uid) {
      throw new Error("Cannot create AuthUser: uid is missing or invalid.");
    }

    return new AuthUser({
      uid: data.uid,
      email: data.email as string | null | undefined,
      displayName: data.displayName as string | null | undefined,
      photoURL: data.photoURL as string | null | undefined,
      emailVerified: (data.emailVerified as boolean | undefined) ?? false,
      isAnonymous: (data.isAnonymous as boolean | undefined) ?? false,
    });
  }
} 